import type {
  CreateCommentInput,
  FeedItem,
  CreatePageInput,
  CreatePostInput,
  MediaAsset,
  Page,
  PageInsights,
  PageProfile,
  UpdatePageInput,
  Post,
  PostComment,
  Shareable,
  TrendingTag,
} from '@talkvibe/shared';

import { api } from './api.ts';

/**
 * Pages, posts and the things people do to them.
 *
 * Reads here work signed out — they are the only calls in the app that do —
 * so nothing in this module assumes a session, and `ignoreUnauthorized` keeps
 * a public read from bouncing a visitor to the sign-in page.
 */
const PUBLIC = { ignoreUnauthorized: true } as const;

export const pageService = {
  /** The tags used most lately. Public, like the posts it counts. */
  trendingTags: (): Promise<{ items: TrendingTag[] }> =>
    api.get<{ items: TrendingTag[] }>('/api/tags/trending', PUBLIC),

  /** The posts carrying one tag. Only ones anybody may read. */
  postsByTag: (tag: string, cursor?: string): Promise<Page<Post>> => {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return api.get<Page<Post>>(`/api/tags/${encodeURIComponent(tag)}/posts${query}`, PUBLIC);
  },

  bySlug: (slug: string): Promise<PageProfile> =>
    api.get<PageProfile>(`/api/pages/${encodeURIComponent(slug)}`, PUBLIC),

  create: (input: CreatePageInput): Promise<PageProfile> =>
    api.post<PageProfile>('/api/pages', input),

  mine: (): Promise<PageProfile[]> => api.get<PageProfile[]>('/api/me/pages'),

  /**
   * The photos somebody posted on their own timeline.
   *
   * Public like the profile, and gated by the same visibility rule — a hidden
   * profile's photos answer the way a missing profile does.
   */
  profileMedia: (username: string, cursor?: string): Promise<Page<MediaAsset>> => {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return api.get<Page<MediaAsset>>(
      `/api/users/${encodeURIComponent(username)}/media${query}`,
      PUBLIC,
    );
  },

  /** The pages somebody follows. Unlisted pages are left out server-side. */
  followedPages: (username: string, cursor?: string): Promise<Page<PageProfile>> => {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return api.get<Page<PageProfile>>(
      `/api/users/${encodeURIComponent(username)}/pages${query}`,
      PUBLIC,
    );
  },

  /**
   * Uploads a page's picture or its banner, and returns the updated page.
   *
   * Reuses the `avatar` and `cover` kinds server-side: a page's picture is an
   * avatar and its banner is a cover, same shapes and same pipeline.
   */
  uploadImage: (slug: string, which: 'avatar' | 'cover', file: File): Promise<PageProfile> => {
    const form = new FormData();
    form.append('file', file);
    return api.post<PageProfile>(`/api/pages/${encodeURIComponent(slug)}/${which}`, form);
  },

  /** Changes a page. Only the fields sent are written. */
  update: (slug: string, input: UpdatePageInput): Promise<PageProfile> =>
    api.patch<PageProfile>(`/api/pages/${encodeURIComponent(slug)}`, input),

  /**
   * A page's insights. Owner only; anybody else gets the same 404 a missing page
   * gives.
   */
  insights: (slug: string): Promise<PageInsights> =>
    api.get<PageInsights>(`/api/pages/${encodeURIComponent(slug)}/insights`),

  follow: (slug: string): Promise<PageProfile> =>
    api.post<PageProfile>(`/api/pages/${encodeURIComponent(slug)}/follow`),

  unfollow: (slug: string): Promise<PageProfile> =>
    api.delete<PageProfile>(`/api/pages/${encodeURIComponent(slug)}/follow`),

  posts: (slug: string, cursor?: string): Promise<Page<Post>> => {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return api.get<Page<Post>>(`/api/pages/${encodeURIComponent(slug)}/posts${query}`, PUBLIC);
  },

  createPost: (slug: string, input: CreatePostInput): Promise<Post> =>
    api.post<Post>(`/api/pages/${encodeURIComponent(slug)}/posts`, input),

  deletePost: (slug: string, postId: string): Promise<void> =>
    api.delete<void>(`/api/pages/${encodeURIComponent(slug)}/posts/${postId}`),

  like: (postId: string): Promise<Post> => api.post<Post>(`/api/posts/${postId}/like`),
  unlike: (postId: string): Promise<Post> => api.delete<Post>(`/api/posts/${postId}/like`),

  /**
   * Saves or unsaves a post.
   *
   * `PUT`, not `POST`: saving twice is saving once, and the server treats it that
   * way, so the method should say so. Returns the post, like a like does, so the
   * card re-renders from the server's answer rather than from a guess.
   */
  /**
   * Reposts a post, or takes it back. Returns the **original** either way, so a
   * card re-renders from the server's answer rather than guessing the new count.
   */
  repost: (postId: string): Promise<Post> => api.put<Post>(`/api/posts/${postId}/repost`),
  unrepost: (postId: string): Promise<Post> => api.delete<Post>(`/api/posts/${postId}/repost`),

  save: (postId: string): Promise<Post> => api.put<Post>(`/api/posts/${postId}/save`),
  unsave: (postId: string): Promise<Post> => api.delete<Post>(`/api/posts/${postId}/save`),

  /** The caller's own bookmarks. There is no such thing as somebody else's. */
  saved: (cursor?: string): Promise<Page<Post>> => {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return api.get<Page<Post>>(`/api/me/saved${query}`);
  },

  /** Without `mediaId`, the post's own thread; with it, that photo's. */
  comments: (postId: string, cursor?: string, mediaId?: string): Promise<Page<PostComment>> => {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    if (mediaId) params.set('mediaId', mediaId);
    const query = params.toString();
    return api.get<Page<PostComment>>(
      `/api/posts/${postId}/comments${query ? `?${query}` : ''}`,
      PUBLIC,
    );
  },

  comment: (postId: string, input: CreateCommentInput): Promise<PostComment> =>
    api.post<PostComment>(`/api/posts/${postId}/comments`, input),

  deleteComment: (commentId: string): Promise<void> =>
    api.delete<void>(`/api/comments/${commentId}`),

  /* ---- Profile posts ---- */

  profilePosts: (username: string, cursor?: string): Promise<Page<Post>> => {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return api.get<Page<Post>>(
      `/api/users/${encodeURIComponent(username)}/posts${query}`,
      PUBLIC,
    );
  },

  createProfilePost: (input: CreatePostInput): Promise<Post> =>
    api.post<Post>('/api/me/posts', input),

  /**
   * A note on somebody else's profile. Friends only, enforced server-side.
   *
   * Posted to the same path the timeline is read from, so the wall is named by
   * the URL rather than by a field in the body that the server would have to
   * trust.
   */
  createWallPost: (username: string, input: CreatePostInput): Promise<Post> =>
    api.post<Post>(`/api/users/${encodeURIComponent(username)}/posts`, input),

  /**
   * Uploads one photo and returns the asset id to attach.
   *
   * Separate from creating the post, because the image has to exist before a
   * post can name it. The id alone is not permission to use it: the server
   * checks owner and kind again when it is attached.
   */
  uploadPostImage: (file: File): Promise<{ id: string; url: string }> => {
    const form = new FormData();
    form.append('file', file);
    return api.post<{ id: string; url: string }>('/api/me/post-image', form);
  },

  /** Likes or unlikes one photo of a post. Returns the post, recounted. */
  setMediaLike: (postId: string, mediaId: string, liked: boolean): Promise<Post> => {
    const path = `/api/posts/${encodeURIComponent(postId)}/media/${encodeURIComponent(mediaId)}/like`;
    return liked ? api.post<Post>(path) : api.delete<Post>(path);
  },

  /** Casts, changes or withdraws a vote. An empty list withdraws it. */
  votePoll: (postId: string, optionIds: string[]): Promise<Post> =>
    api.post<Post>(`/api/posts/${encodeURIComponent(postId)}/vote`, { optionIds }),

  deleteProfilePost: (postId: string): Promise<void> =>
    api.delete<void>(`/api/me/posts/${postId}`),

  /* ---- Home feed ---- */

  feed: (cursor?: string): Promise<Page<FeedItem>> => {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return api.get<Page<FeedItem>>(`/api/feed${query}`);
  },

  likeComment: (commentId: string): Promise<PostComment> =>
    api.post<PostComment>(`/api/comments/${commentId}/like`),

  unlikeComment: (commentId: string): Promise<PostComment> =>
    api.delete<PostComment>(`/api/comments/${commentId}/like`),

  /* ---- Group feed ---- */

  groupPosts: (groupId: string, cursor?: string): Promise<Page<Post>> => {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return api.get<Page<Post>>(`/api/groups/${groupId}/posts${query}`);
  },

  /** Every photo posted in a group, for the media tab. Members only. */
  groupMedia: (groupId: string, cursor?: string): Promise<Page<MediaAsset>> => {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return api.get<Page<MediaAsset>>(`/api/groups/${groupId}/media${query}`);
  },

  createGroupPost: (groupId: string, input: CreatePostInput): Promise<Post> =>
    api.post<Post>(`/api/groups/${groupId}/posts`, input),

  deleteGroupPost: (postId: string): Promise<void> =>
    api.delete<void>(`/api/groups/posts/${postId}`),

  shareable: (postId: string): Promise<Shareable> =>
    api.get<Shareable>(`/api/posts/${postId}/share`, PUBLIC),
};
